/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Styles from './Stimpage.module.css';
import {
  getHormonalGraphData,
  getStim,
  Stim,
} from './helper.stim';
import { convertToLocalDateTime } from '../../services/time';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  ChartDataset,
  ChartData,
  TooltipItem,
  ChartOptions,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box
} from '@mui/material';
import { HormonalGraphResponse } from '../../types';
import { useVerboseLog } from '../../utils/debug';
import { useTranslation } from 'react-i18next';
import { ReactComponent as NoDataIcon } from '../../assets/SVG/NoAccess.svg';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController
);

export interface HormonalProps {
  patientId: number;
  stim: number;
}

const HormonalGraph: React.FC<HormonalProps> = ({ patientId, stim }) => {
  const verboseLog = useVerboseLog();
  const { t } = useTranslation('cycleOverview_config');

  const [firstDate, setFirstDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedStim, setSelectedStim] = useState<Stim | null>(null);
  const [hormones, setHormones] = useState<HormonalGraphResponse[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [datasets, setDatasets] = useState<ChartDataset<'line' | 'bar', number[]>[]>([]);
  const [selectedHormone, setSelectedHormone] = useState<'Progesterone' | 'Estradiol' | 'FSH' | 'LH' | 'All'>('All');

  const hormoneColors = useMemo(() => [
    'rgb(255, 99, 132)',
    'rgb(75, 192, 192)',
    '#FFCE56',
    '#9966FF'
  ], []);

  const fetchStimData = useCallback(async () => {
    if (!stim) return;
    try {
      const stimData = await getStim(stim);
      setSelectedStim(stimData);
    } catch (error) {
      verboseLog('Failed to fetch Stim data:', error);
    }
  }, [stim, verboseLog]);

  const fetchHormoneData = useCallback(async () => {
    if (!patientId || !stim) return;
    try {
      const results = await getHormonalGraphData(patientId, stim, true);
      setHormones(results.objects || []);
    } catch (error) {
      verboseLog('Failed to fetch test results:', error);
    }
  }, [patientId, stim, verboseLog]);

  useEffect(() => {
    fetchStimData();
  }, [fetchStimData]);

  useEffect(() => {
    if (selectedStim?.cycle) {
      const { cycle_start_date, cycle_end_date } = selectedStim.cycle;
      if (cycle_start_date) setFirstDate(convertToLocalDateTime(cycle_start_date));
      if (cycle_end_date) setEndDate(convertToLocalDateTime(cycle_end_date));
    }
  }, [selectedStim]);

  useEffect(() => {
    if (firstDate && stim && patientId) fetchHormoneData();
  }, [firstDate, endDate, fetchHormoneData, patientId, stim]);

  useEffect(() => {
    if (!hormones.length) return;

    const cycleLabels = Array.from(new Set(hormones.map(h => h.cycle_day?.toString()))).sort((a, b) => Number(a) - Number(b));
    setLabels(cycleLabels);

    const testNames = Array.from(new Set(hormones.map(r => r.laboratory_test.name)));

    
    // Helper function to get hormone result for a given day and test
    const getHormoneResult = (day: string, test: string) => {
      const result = hormones.find(
        r => r.cycle_day === Number(day) && r.laboratory_test.name === test
      );
      return result?.results ?? 0;
    };

    // Define color codes for hormones
    const hormoneColorMap = {
      'Estradiol (E2)': '#6A7289',
      'Luteinizing Hormone (LH)': '#949BB1',
      'FSH': '#ABB0C3',
      'Progesterone': '#C2C6D5',
      'Estradiol': '#DADCE6',
      'Anti-Müllerian Hormone (AMH)': '#F3F3F7',
      // Add more hormone names as needed
    };

    const generatedDatasets = testNames.map((test, index) => {
      return {
        type: 'bar' as const,
        label: test,
        borderColor: (hormoneColorMap as Record<string, string>)[test] || '#DADCE6',
        backgroundColor: (hormoneColorMap as Record<string, string>)[test] || '#DADCE6',
        hoverBackgroundColor: (hormoneColorMap as Record<string, string>)[test] || '#6A7289',
        hoverBorderColor: (hormoneColorMap as Record<string, string>)[test] || '#6A7289',
        borderWidth: 2,
        borderRadius: 16,
        data: cycleLabels.map(day => getHormoneResult(day, test)),
      };
    });

    const filtered = selectedHormone === 'All'
      ? generatedDatasets
      : generatedDatasets.filter(d => d.label.includes(selectedHormone));

    setDatasets(filtered);
  }, [hormones, selectedHormone, hormoneColors]);

  const chartData: ChartData<'bar' | 'line', number[], string> = {
    labels,
    datasets
  };

  const chartOptions : ChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' as const },
      tooltip: {
        position:'average' as const,
        backgroundColor: '#fff',
        borderColor: '#F28C7E',
        borderWidth: 1,
        titleColor: '#F28C7E',
        titleFont: { size: 14, weight: 'bold' as const },
        titleAlign: 'left' as const,
        padding: 12,
        displayColors: false,
        bodyColor: '#000',
        bodyFont: { size: 13 },
        callbacks: {
          title: () => 'Actual Value',
          beforeBody: (tooltipItems: TooltipItem<'bar' | 'line'>[]) => {
            const item = tooltipItems[0];
            return `Name : ${item.dataset.label} \nDay : ${labels[tooltipItems[0].dataIndex]}`;
          },
          label: (context: TooltipItem<'bar' | 'line'>) => {
            // Find the hormone object for this label and dataset
            const hormoneObj = hormones.find(
              h => h.cycle_day === Number(labels[context.dataIndex]) && h.laboratory_test.name === context.dataset.label
            );
            const unit = hormoneObj?.laboratory_test.unit || '';
            return `Value : ${context.parsed.y}${unit ? ' ' + unit : ''}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t('days'),
          font: { size: 14, weight: 'bold' },
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t('value'), // removed hardcoded unit
          font: { size: 14, weight: 'bold' },
        },
        ticks: {
          stepSize: 10,
          callback: function(
            tickValue: string | number,
            index: number,
            ticks: import('chart.js').Tick[]
          ) {
            return `${tickValue}`;
          }
        }
      }
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'Progesterone' | 'Estradiol' | 'FSH' | 'LH' | 'All';
    setSelectedHormone(value);
  };

  return (
    <div className={Styles.hormonal}>
      <FormControl>
        <RadioGroup
          row
          name="hormone-select"
          onChange={handleRadioChange}
          defaultValue="All"
        >
          {['All', 'Progesterone', 'Estradiol', 'FSH', 'LH'].map(hormone => (
            <FormControlLabel key={hormone} value={hormone} control={<Radio />} label={t(hormone)} />
          ))}
        </RadioGroup>
      </FormControl>
    
      {datasets.length && labels.length ? (
        <Chart type="bar" data={chartData} options={chartOptions} />
        
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
          <NoDataIcon />
          <Typography variant="h6" color="textSecondary" style={{ marginTop: '16px' }}>
            {t('NoDataToDisplay')}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default HormonalGraph;

// import React, { useEffect, useState, useCallback, useMemo } from 'react';
// import Styles from './Stimpage.module.css';
// import {
//   getHormonalGraphData,
//   getStim,
//   Stim,
// } from './helper.stim';
// import { convertToLocalDateTime } from '../../services/time';
// import {
//   Chart as ChartJS,
//   LinearScale,
//   CategoryScale,
//   BarElement,
//   PointElement,
//   LineElement,
//   Legend,
//   Tooltip,
//   LineController,
//   BarController,
//   ChartDataset,
//   ChartData,
//   TooltipItem,
//   ChartOptions,
// } from 'chart.js';
// import { Chart } from 'react-chartjs-2';
// import {
//   FormControl,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   Typography,
//   Box
// } from '@mui/material';
// import { HormonalGraphResponse } from '../../types';
// import { HubRounded } from '@mui/icons-material';

// ChartJS.register(
//   LinearScale,
//   CategoryScale,
//   BarElement,
//   PointElement,
//   LineElement,
//   Legend,
//   Tooltip,
//   LineController,
//   BarController
// );

// export interface HormonalProps {
//   patientId: number;
//   stim: number;
// }

// const HormonalGraph: React.FC<HormonalProps> = ({ patientId, stim }) => {
//   const [firstDate, setFirstDate] = useState<Date | null>(null);
//   const [endDate, setEndDate] = useState<Date | null>(null);
//   const [selectedStim, setSelectedStim] = useState<Stim | null>(null);
//   const [hormones, setHormones] = useState<HormonalGraphResponse[]>([]);
//   const [labels, setLabels] = useState<string[]>([]);
//   const [datasets, setDatasets] = useState<ChartDataset<'line' | 'bar', number[]>[]>([]);
//   const [selectedHormone, setSelectedHormone] = useState<'Progesterone' | 'Estradiol' | 'FSH' | 'LH' | 'All'>('All');

//   const hormoneColors = useMemo(() => [
//     'rgb(255, 99, 132)',
//     'rgb(75, 192, 192)',
//     '#FFCE56',
//     '#9966FF'
//   ], []);

//   const fetchStimData = useCallback(async () => {
//     if (!stim) return;
//     try {
//       const stimData = await getStim(stim);
//       setSelectedStim(stimData);
//     } catch (error) {
//       verboseLog('Failed to fetch Stim data:', error);
//     }
//   }, [stim]);

//   const fetchHormoneData = useCallback(async () => {
//     if (!patientId || !stim) return;
//     try {
//       const results = await getHormonalGraphData(patientId, stim);
//       setHormones(results.objects || []);
//     } catch (error) {
//       verboseLog('Failed to fetch test results:', error);
//     }
//   }, [patientId, stim]);

//   useEffect(() => {
//     fetchStimData();
//   }, [fetchStimData]);

//   useEffect(() => {
//     if (selectedStim?.cycle) {
//       const { cycle_start_date, cycle_end_date } = selectedStim.cycle;
//       if (cycle_start_date) setFirstDate(convertToLocalDateTime(cycle_start_date));
//       if (cycle_end_date) setEndDate(convertToLocalDateTime(cycle_end_date));
//     }
//   }, [selectedStim]);

//   useEffect(() => {
//     if (firstDate && stim && patientId) fetchHormoneData();
//   }, [firstDate, endDate, fetchHormoneData, patientId, stim]);

//   useEffect(() => {
//     if (!hormones.length) return;

//     const cycleLabels = Array.from(new Set(hormones.map(h => h.cycle_day.toString()))).sort((a, b) => Number(a) - Number(b));
//     setLabels(cycleLabels);

//     const testNames = Array.from(new Set(hormones.map(r => r.laboratory_test.name)));

//     // Helper function to get hormone result for a given day and test
//     const getHormoneResult = (day: string, test: string) => {
//       const result = hormones.find(
//         r => r.cycle_day === Number(day) && r.laboratory_test.name === test
//       );
//       return result?.results ?? 0;
//     };

//     const generatedDatasets = testNames.map((test, index) => {
//       return {
//         type: 'bar' as const,
//         label: test,
//         borderColor: hormoneColors[index % hormoneColors.length],
//         backgroundColor: hormoneColors[index % hormoneColors.length],
//         borderWidth: 2,
//         borderRadius: 16,
//         data: cycleLabels.map(day => getHormoneResult(day, test)),
//       };
//     });

//     const hormoneReferenceRanges: Array<{ name: string; values: { maxDay: number; avg: number }[] }> = [
//        {
//         name:'Progesterone',
//         values:[
//           {
//             maxDay: 5,
//             avg: 0.3
//           },
//           {
//             maxDay: 13,
//             avg: 1
//           },
//           {
//             maxDay: 14,
//             avg: 1.5
//           },
//           {
//             maxDay: 28,
//             avg: 25
//           },
//         ],
//       },
//       {
//         name: 'Estradiol',
//         values:[
//           {
//             maxDay: 5,
//             avg: 75
//           },
//           {
//             maxDay: 13,
//             avg: 200
//           },
//           {
//             maxDay: 14,
//             avg: 500
//           },
//           {
//             maxDay: 28,
//             avg: 300
//           },
//         ],
//       },
      
//     ]
    
//     const daysToAverage = selectedHormone!== 'All' && hormones.filter(h => {if(h.laboratory_test.name === selectedHormone){return h.cycle_day}} );
      
//     // Calculate average values for each label (cycle day)
    
//     const avgSet = hormoneReferenceRanges.find((hr)=>{
//       if(selectedHormone.includes(hr.name)){
//         return hr.values;
//       }
//     });

//     const resavg = Array.isArray(daysToAverage) ? daysToAverage.map((dta) => {
//       avgSet?.values.find((avg) => {
//         if(dta.cycle_day <= avg.maxDay){
//           return { [dta.cycle_day]: avg.avg };
//         }
//       });
//     }) : [];  
    
//     console.log(resavg);

//     // Add average line dataset
//     const averageDataset = {
//       type: 'line' as const,
//       label: 'Average Value',
//       borderColor: 'red',
//       backgroundColor: 'transparent',
//       borderWidth: 2,
//       fill: false,
//       data: resavg,
//       tension: 0.1
//     };

//     const filtered = selectedHormone === 'All'
//       ? [...generatedDatasets,]
//       : [...generatedDatasets.filter(d => d.label.includes(selectedHormone))];

//     setDatasets(filtered);
//   }, [hormones, selectedHormone, hormoneColors]);

//   const chartData: ChartData<'bar' | 'line', number[], string> = {
//     labels,
//     datasets
//   };

//   const chartOptions: ChartOptions = {
//     responsive: true,
//     plugins: {
//       legend: { position: 'top' as const },     
//       tooltip: {
//         position: 'average' as const,
//         backgroundColor: '#fff',
//         borderColor: '#F28C7E',
//         borderWidth: 1,
//         titleColor: '#F28C7E',
//         titleFont: { size: 14, weight: 'bold' as const },
//         titleAlign: 'left' as const,
//         padding: 12,
//         displayColors: false,
//         bodyColor: '#000',
//         bodyFont: { size: 13 },
//         callbacks: {
//           title: () => 'Actual Value',          
//           beforeBody: (tooltipItems: TooltipItem<'bar' | 'line'>[]) => {
//             const item = tooltipItems[0];
//             return `Name : ${item.dataset.label} \nDay : ${labels[tooltipItems[0].dataIndex]}`;
//           },         
//           label: (context: TooltipItem<'bar' | 'line'>) => {
//             return `Value : ${context.parsed.y} ng/ml`;},
//         },
//       },
//     },
//     scales: {
//       y: {
//         beginAtZero: true,
//         ticks: {
//           stepSize: 10,
//           callback: function(
//             tickValue: string | number,
//             index: number,
//             ticks: import('chart.js').Tick[]
//           ) {
//             return `${tickValue}`;
//           }
//         }
//       }
//     }
//   };

//   const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const value = event.target.value as 'Progesterone' | 'Estradiol' | 'FSH' | 'LH' | 'All';
//     setSelectedHormone(value);
//   };

//   return (
//     <div className={Styles.hormonal}>
//       <FormControl>
//         <RadioGroup
//           row
//           name="hormone-select"
//           onChange={handleRadioChange}
//           defaultValue="All"
//         >
//           {['All', 'Progesterone', 'Estradiol', 'FSH', 'LH'].map(hormone => (
//             <FormControlLabel key={hormone} value={hormone} control={<Radio />} label={hormone} />
//           ))}
//         </RadioGroup>
//       </FormControl>

//       {datasets.length && labels.length ? (
//         <Chart type="bar" data={chartData} options={chartOptions} />
//       ) : (
//         <Box mt={4} display="flex" justifyContent="center">
//           <Typography>No data to display</Typography>
//         </Box>
//       )}
//     </div>
//   );
// };

// export default HormonalGraph;
